// ============================================================================
// L'EFFET D'UNE CAMPAGNE — ce que l'écran dit de l'écart entre les destinataires servis et le groupe témoin.
//
// UTILITÉ : deux écrans en parlent — la tuile Campagnes du tableau de bord et la fiche d'une campagne. Écrite deux fois,
// la phrase dirait deux choses du même écart, et seule l'une des deux suivrait la prochaine correction.
// ============================================================================
import type { CampaignLift } from '../api';
import { t } from '../i18n';

/**
 * Un écart en points de base, en points signés au dixième : « +5,2 », « -5,0 », « 0,0 ».
 * ⚠️ PARTAGÉ PAR LA PHRASE ET LA CELLULE DES STATISTIQUES : deux écritures du même écart finiraient par différer.
 */
export function signedPoints(bp: number): string {
  const v = (bp / 100).toFixed(1).replace('.', ',');
  return bp > 0 ? `+${v}` : v;
}

/**
 * L'ÉCART AU GROUPE TÉMOIN, EN UNE PHRASE — ou `null` quand il n'y a rien à en dire.
 *
 * ⚠️ 🔴 C'EST LA SEULE LIGNE DE CET ÉCRAN QUI PARLE DE CAUSE, et donc celle qui doit le moins
 * affirmer. Un écart ne s'affiche NU que s'il est concluant ; sinon la phrase dit POURQUOI on ne
 * conclut pas, et les deux raisons appellent deux gestes opposés — « trop peu de monde » invite à
 * attendre ou à élargir, « aucun écart mesurable » dit que la campagne, elle, n'a rien changé.
 *
 * ⚠️ L'INTERVALLE EST ÉCRIT, PAS SOUS-ENTENDU. « +10,0 points » seul se lit comme une mesure exacte ;
 * « entre +5,2 et +14,8 » dit ce qu'on sait vraiment. C'est plus long, et c'est la différence entre
 * un chiffre et une information.
 *
 * ⚠️ UN ÉCART NÉGATIF SE DIT AUSSI. Rare, et pas absurde : un message mal reçu fait fuir. Le taire
 * « parce qu'un lift négatif n'a pas de sens » effacerait le seul signal qui dit d'arrêter.
 *
 * ⚠️ RIEN ICI NE RECALCULE : l'écart et son intervalle viennent du serveur. Une seconde définition
 * divergerait de la première, et personne ne saurait laquelle a raison.
 */
export function phraseEcart(a: { lift: CampaignLift | null; holdout: { size: number; buyers: number } | null } | null): string | null {
  if (!a?.lift || !a.holdout) return null;
  const pts = signedPoints;
  if (a.lift.conclusive) {
    return t('{v} points d’acheteurs contre le groupe témoin (entre {low} et {high})', { v: pts(a.lift.pointsBp), low: pts(a.lift.lowBp), high: pts(a.lift.highBp) });
  }
  if (a.lift.reason === 'inconclusive') {
    return t('aucun écart mesurable avec le groupe témoin ({temoins})', { temoins: t('{n} témoin', { n: a.holdout.size }) });
  }
  // ⚠️ ON DONNE LES DEUX NOMBRES QUI MANQUENT. « Trop peu de monde » sans dire combien laisse
  //    l'exploitant sans idée de l'effort à fournir — 20 témoins et 2 000 n'appellent pas le même geste.
  // ⚠️ DEUX PHRASES COMPTÉES, ET `{n}` DÉSIGNE CHAQUE FOIS LA VARIABLE QUI S'ACCORDE : la première
  //    comptait avec `{size}`, que rien ne reconnaît comme un compteur — l'écran aurait dit
  //    « 25 témoin » pour toujours, et aucune garde de traduction ne l'aurait vu.
  // ⚠️ DEUX COMPTES, UNE PHRASE : le moteur n'accorde que `{n}`, donc le second groupe est une clé COMPTÉE à
  //    part, passée en valeur — jamais un fragment collé derrière (le traducteur recevait « , {n} acheteur »).
  return t('groupe témoin trop petit pour conclure : {n} témoin, {acheteurs}', { n: a.holdout.size, acheteurs: t('{n} acheteur', { n: a.holdout.buyers }) });
}
